import { Dialog } from '@/components/UI/Dialog'
import { HtmlContent } from '@/components/UI/HtmlContent'
import { memo } from 'react'
import { useTranslation } from 'react-i18next'

type Props = {
  onClose: () => void
  /** storybookで確認する際に、スクロール可にするためのフラグ。raundで利用する場合にはtrue */
  scrollLock?: boolean
  /** 管理画面で登録したクイズタイトル。未指定時は i18n フォールバック「クイズ」を使用 */
  title?: string | null
  /** クイズの問題文 */
  question: string
  /** クイズ画像URL */
  image?: string | null
  /** 選択肢リスト */
  choices: { id: number; item: string | null }[]
  /** 選択肢を選んだときのコールバック（選択肢IDを返す） */
  onSelect: (choiceId: number) => void
  /** API呼び出し中フラグ（trueのとき選択肢を無効化） */
  isLoading?: boolean
}

/**
 * `Raund-Replace / Screen UI / SPOT-DETAIL-スタンプ獲得（クイズ）`のダイアログコンポーネント
 * Figma node: 439:7852
 */
export const DialogQuizChallenge = memo(
  ({ onClose, scrollLock = false, title, question, image, choices, onSelect, isLoading = false }: Props) => {
    const { t } = useTranslation()

    return (
      <Dialog
        selectorState={true}
        compact
        showCloseButton={true}
        closeButtonInline
        lockScroll={scrollLock}
        closeAction={onClose}
        title={title ?? t('spot_detail.quiz.title', 'クイズ')}
      >
        <div data-testid="dialog-quiz-challenge">
          {image && (
            <div className="mb-[15px] overflow-hidden">
              <img src={image} alt="" className="w-full h-[168px] object-cover" />
            </div>
          )}

          <HtmlContent html={question} variant="subtitle1" className="mb-[15px] font-bold text-black" />

          <ul className="flex flex-col gap-[10px]">
            {choices.map((choice) => (
              <li key={choice.id}>
                <button
                  type="button"
                  onClick={() => onSelect(choice.id)}
                  disabled={isLoading}
                  // 選択肢の文言は管理画面で自由入力（多言語化すると特に長くなる）ため、
                  // 高さは固定せず内容に追従させる。border-box なので
                  // border(1+1) + py(8+8) + leading(18) = 36px となり、1行時は従来の高さを維持する（#3529）
                  className="w-full min-h-[36px] px-[16px] py-[8px] bg-white border border-[#757575] rounded-[8px] text-[#757575] text-[13px] font-bold leading-[18px] text-center whitespace-normal break-words disabled:opacity-50"
                >
                  {choice.item ?? ''}
                </button>
              </li>
            ))}
          </ul>
        </div>
      </Dialog>
    )
  },
)
